/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../services';
import { authService } from '../services';
import type { User, Role } from '../types';
import { useAuthStore } from '../store';

const USERS_KEY = 'users';

export function useUsers(enabled = true) {
  return useQuery({
    queryKey: [USERS_KEY],
    queryFn: () => userService.list(),
    enabled,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<User> }) => userService.update(id, updates),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_KEY] }),
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      email: string;
      name: string;
      password: string;
      role: Role;
      company?: string;
      phone?: string;
    }) => userService.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_KEY] }),
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      email: string;
      role: Role;
      name?: string;
      company?: string;
      phone?: string;
      message?: string;
    }) => userService.invite(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_KEY] }),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_KEY] }),
  });
}

export function useAdminResetPassword() {
  return useMutation({
    mutationFn: (userId: string) => userService.adminResetPassword(userId),
  });
}

export function useDisableUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.disable(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_KEY] }),
  });
}

export function useEnableUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.enable(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_KEY] }),
  });
}

export function useResendVerificationEmail() {
  return useMutation({
    mutationFn: (id: string) => userService.resendVerification(id),
  });
}

export function useResendInviteEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.resendInvite(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_KEY] }),
  });
}

export function useUserDemandSources(userId: string | null) {
  return useQuery({
    queryKey: ['user-demand-sources', userId],
    queryFn: () => userService.getDemandSources(userId as string),
    enabled: !!userId,
    retry: false,
  });
}

export function useUpdateMyProfile() {
  const queryClient = useQueryClient();
  const updateUser = useAuthStore((s) => s.updateUser);

  return useMutation({
    mutationFn: (updates: { name?: string; company?: string; phone?: string }) => authService.updateMyProfile(updates),
    onSuccess: (updated) => {
      updateUser(updated);
      queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
    },
  });
}

export function useResendMyVerification() {
  return useMutation({
    mutationFn: () => authService.resendMyVerification(),
  });
}

export function useChangeMyPassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) => authService.changeMyPassword(input),
  });
}
